import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';

import { getDecimal } from 'src/utils/utils';
import { Layout } from 'src/styles';
import { useAPIWrapper } from 'drf-react-by-schema';

import { useAppSelector } from 'src/app/hooks';

import { HomeTotalsQuebradeiraType } from 'src/@types';
import { getProductionZoneLabel, ProductionZone } from 'src/constants/ProductionZone';
import { AzeiteOrOleoProductionStatus, FlocosProductionStatus } from 'src/constants';
import { ProductionTimeRange } from 'src/constants/ProductionTimeRange';

import HomeTotalsCard from './HomeTotalsCard';
import HomeTotalsCardGroup from './HomeTotalsCardGroup';

export default function HomeTotalsQuebradeira() {
  const { perfil, isMobile } = useAppSelector((state) => state.page);
  const { getRawData } = useAPIWrapper();
  const location = useLocation();

  const [totals, setTotals] = useState<HomeTotalsQuebradeiraType>();
  const [timeRange, setTimeRange] = useState<ProductionTimeRange>(ProductionTimeRange.ALL);

  const [pendentes, pendentesStr] = useMemo(() => {
    if (!totals || !totals[ProductionZone.SELF] || !perfil) {
      return [undefined, undefined];
    }

    const totalsSelf = totals[ProductionZone.SELF];

    const newPendentes =
      totalsSelf.azeite_pendente.length +
      totalsSelf.oleo_pendente.length +
      totalsSelf.flocos_pendente.length;

    const pendentesStrAr: React.ReactElement[] = [];

    if (totalsSelf.azeite_pendente.length > 0) {
      pendentesStrAr.push(
        <Link
          to={
            totalsSelf.azeite_pendente.length === 1
              ? `/azeite_production/view/${totalsSelf.azeite_pendente[0]}`
              : `/azeite_production/?status=${AzeiteOrOleoProductionStatus.READY_TO_PROCESS}`
          }>
          {totalsSelf.azeite_pendente.length === 1
            ? '1 produção de azeite'
            : `${totalsSelf.azeite_pendente.length} produções
        de azeite`}
        </Link>
      );
    }

    if (totalsSelf.oleo_pendente.length > 0) {
      pendentesStrAr.push(
        <Link
          to={
            totalsSelf.oleo_pendente.length === 1
              ? `/oleo_production/view/${totalsSelf.oleo_pendente[0]}`
              : `/oleo_production/?status=${AzeiteOrOleoProductionStatus.READY_TO_PROCESS}`
          }>
          {totalsSelf.oleo_pendente.length === 1
            ? '1 produção de óleo'
            : `${totalsSelf.oleo_pendente.length} produções de óleo`}
        </Link>
      );
    }

    if (totalsSelf.flocos_pendente.length > 0) {
      pendentesStrAr.push(
        <Link
          to={
            totalsSelf.flocos_pendente.length === 1
              ? `/flocos_production/view/${totalsSelf.flocos_pendente[0]}`
              : `/flocos_production/?status=${FlocosProductionStatus.IN_PRODUCTION}`
          }>
          {totalsSelf.flocos_pendente.length === 1
            ? '1 produção de flocos'
            : `${totalsSelf.flocos_pendente.length} produções
        de flocos`}
        </Link>
      );
    }

    const newPendentesStr = (
      <>
        Você tem{' '}
        {pendentesStrAr.map((pendente, index) => (
          <React.Fragment key={index}>
            {index > 0 && index === pendentesStrAr.length - 1 && ' e '}
            {index > 0 && index < pendentesStrAr.length - 1 && ', '}
            {pendente}
          </React.Fragment>
        ))}{' '}
        {pendentesStrAr.length === 1
          ? 'já iniciada que precisa ser concluída.'
          : 'já iniciadas que precisam ser concluídas.'}
      </>
    );

    return [newPendentes, newPendentesStr];
  }, [totals, perfil]);

  const getNomeColetivo = useCallback(
    (productionZone: ProductionZone) => {
      if (!perfil) {
        return;
      }
      switch (productionZone) {
        case ProductionZone.GROUP:
          return perfil.grupo?.label;
        case ProductionZone.COMMUNITY:
          return perfil.comunidade?.nome;
      }
      return;
    },
    [perfil]
  );

  useEffect(() => {
    if (!perfil) {
      return;
    }
    const retrieveData = async () => {
      const newTotals = await getRawData(`totals_quebradeira/?time_range=${timeRange}`);
      setTotals(newTotals);
    };
    retrieveData();
  }, [perfil, timeRange, location]);

  if (!totals || !perfil) {
    return null;
  }

  return (
    <Box sx={Layout.homeTotalsInner}>
      {typeof pendentes !== 'undefined' && pendentes > 0 && (
        <Card>
          <CardContent sx={Layout.homeTotalsCardContent}>
            <Box sx={Layout.homeTotalsIconWrapper}>
              <NotificationsNoneIcon sx={Layout.homeTotalsIcon} />
            </Box>
            <Typography sx={{ textAlign: 'center' }}>{pendentesStr}</Typography>
          </CardContent>
        </Card>
      )}
      {Object.values(ProductionZone)
        .filter((item) => item !== ProductionZone.COMMUNITY)
        .map((productionZone) => (
          <React.Fragment key={productionZone}>
            {!!totals[productionZone] && (
              <HomeTotalsCardGroup
                key={productionZone}
                label={
                  getNomeColetivo(productionZone) || getProductionZoneLabel({ productionZone })
                }
                timeRange={timeRange}
                setTimeRange={setTimeRange}
                hasAllTimeRangeLabel={true}
                hasTimeRangeMenu={true}
                isMobile={isMobile}>
                <HomeTotalsCard
                  key="azeite"
                  label="Azeite"
                  value={getDecimal(totals[productionZone].azeite_produzido, 'litros')}
                  seeMoreTarget="/azeite_production"
                  isMobile={isMobile}
                />

                <HomeTotalsCard
                  key="oleo"
                  label="Óleo"
                  value={getDecimal(totals[productionZone].oleo_produzido, 'litros')}
                  seeMoreTarget="/oleo_production"
                  isMobile={isMobile}
                />

                <HomeTotalsCard
                  key="flocos"
                  label="Flocos"
                  value={getDecimal(totals[productionZone].flocos_produzido, 'kg')}
                  seeMoreTarget="/flocos_production"
                  isMobile={isMobile}
                />
              </HomeTotalsCardGroup>
            )}
          </React.Fragment>
        ))}
    </Box>
  );
}
